import { useTranslation } from 'react-i18next';
import { X, ChevronRight } from 'lucide-react';

import { usePersona } from '@/hooks/usePersona';
import { pathFor } from '@/data/routes.js';

/**
 * PersonaBadge — small persistent chip showing the visitor's detected
 * persona, with a quick way to reset / re-take the quiz.
 *
 * 2026-05-26 — Sprint 8 (Personas).
 *
 * Where it shows :
 *   - Top-right of the header (mobile : in the mobile menu)
 *   - Only renders when a persona is set (silent otherwise)
 *
 * What it does :
 *   - Visual signal : "we know who you are, content is tuned to you"
 *   - One-click clear (×) to wipe persona + intent + pet flags
 *   - Click on the body : opens /par-ou-commencer to re-pick
 *
 * Accent color uses the persona's CSS variable (--accent) so the chip
 * reflects the assigned identity without a switch statement.
 *
 * @param {object} props
 * @param {'pill'|'inline'} [props.variant='pill']  pill = floating chip, inline = no bg
 * @param {string} [props.className]
 */
export default function PersonaBadge({ variant = 'pill', className = '' }) {
  const { t, i18n } = useTranslation();
  const { persona, reset, petOwner } = usePersona();
  const lang = ['fr', 'en', 'es'].includes(i18n.language) ? i18n.language : 'fr';

  if (!persona) return null;

  const label = persona.label[lang] || persona.label.fr;
  const accentBg = `${persona.accentHex}1A`;   // 10 % alpha
  const accentBorder = `${persona.accentHex}55`;

  if (variant === 'inline') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-sm font-medium ${className}`}
        style={{ color: persona.accentHex }}
      >
        <span aria-hidden="true">{persona.emoji}</span>
        {label}
        {petOwner && <span aria-hidden="true" title="Pet owner"> · 🐕</span>}
      </span>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${className}`}
      style={{
        backgroundColor: accentBg,
        borderColor: accentBorder,
        color: persona.accentHex,
      }}
    >
      <span aria-hidden="true">{persona.emoji}</span>
      <span>{label}</span>
      {petOwner && <span aria-hidden="true" title="Pet owner">🐕</span>}
      <a
        href={pathFor('startHere', lang)}
        className="inline-flex items-center text-current opacity-70 hover:opacity-100"
        aria-label={t('common:personaChange', { defaultValue: 'Changer de profil' })}
      >
        <ChevronRight size={12} aria-hidden="true" />
      </a>
      <button
        type="button"
        onClick={reset}
        aria-label={t('common:personaClear', { defaultValue: 'Effacer mon profil' })}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-current/0 text-current opacity-50 transition-opacity hover:opacity-100"
      >
        <X size={11} aria-hidden="true" />
      </button>
    </div>
  );
}
